import { useEffect, useState } from "react";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Shown at the top of the Artist / Label Overview when the signed-in user
 * does not yet have an active paid subscription. Clicking the CTA jumps to
 * the Billing tab and — via BillingPanel's ?tier= consumer — auto-launches
 * the Stripe checkout for the recommended plan.
 *
 * Dismissible for the current browser session only (sessionStorage), so it
 * resurfaces on the next login as long as the user is still on the free tier.
 */
export default function UpgradeBanner({ recommendedTier = "artist", goto }) {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      if (window.sessionStorage.getItem("noizbloc_upgrade_dismissed") === "1") {
        setDismissed(true);
      }
    } catch (e) { /* ignore */ }
    (async () => {
      try {
        const { data } = await api.get("/billing/subscription");
        setSub(data);
      } catch (e) { /* ignore */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dismiss = () => {
    try { window.sessionStorage.setItem("noizbloc_upgrade_dismissed", "1"); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  const goUpgrade = () => {
    // Ask BillingPanel to auto-launch the Stripe checkout for this tier.
    try { window.localStorage.setItem("noizbloc_pending_tier", recommendedTier); } catch (e) { /* ignore */ }
    if (typeof goto === "function") goto("billing");
  };

  if (loading || dismissed) return null;
  if (sub?.active) return null;

  const tierLabel = { artist: "Artist — £19/yr", pro: "Pro — £29/yr", label: "Label — £99/yr" }[recommendedTier] || "a paid plan";

  return (
    <div
      data-testid="upgrade-banner"
      className="relative overflow-hidden rounded-3xl border border-blue-500/30 bg-gradient-to-br from-blue-600/15 via-blue-500/[0.06] to-transparent p-6 md:p-7"
    >
      <button
        type="button"
        data-testid="upgrade-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-4 right-4 text-slate-400 hover:text-white p-1.5 rounded-full hover:bg-white/5 transition-colors"
      >
        <X size={16} />
      </button>
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="h-12 w-12 rounded-2xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0">
          <Sparkles size={22} className="text-blue-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-blue-300">Unlock distribution</div>
          <div className="mt-1 font-display text-xl md:text-2xl text-white tracking-tight">
            Pick a plan to send your music to 150+ stores worldwide
          </div>
          <p className="mt-2 text-sm text-slate-300 max-w-2xl">
            Your account is ready — the last step is choosing a subscription. We recommend the <span className="text-white font-medium">{tierLabel}</span> plan to get started.
            Cancel anytime from your Billing tab.
          </p>
        </div>
        <button
          type="button"
          data-testid="upgrade-banner-cta"
          onClick={goUpgrade}
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-6 py-3 hover:bg-slate-200 active:scale-[0.98] transition-all shrink-0"
        >
          Upgrade now <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
}
